import { MAX_MONITORS_ALLOWED } from '../constants';
import { CheckResult } from '../types';

function estimateScreenCount(): number {
  const { width, availWidth } = window.screen;
  if (availWidth > width * 1.5) return 2;
  return 1;
}

function isDesktopSafari(): boolean {
  const ua = navigator.userAgent;
  const isSafari = /^((?!chrome|android).)*safari/i.test(ua);
  const isMobile = /iPhone|iPad|iPod/i.test(ua);
  return isSafari && !isMobile;
}

async function tryWindowManagementApi(): Promise<number | null> {
  try {
    const { getScreenDetails } = window as unknown as {
      getScreenDetails?: () => Promise<{ screens: unknown[] }>;
    };

    if (typeof getScreenDetails !== 'function') return null;

    await new Promise<void>(resolve => {
      setTimeout(resolve, 100);
    });

    const details = await getScreenDetails();
    return details.screens.length;
  } catch {
    return null;
  }
}

export async function monitorCheck(): Promise<CheckResult> {
  const timestamp = new Date().toISOString();

  try {
    const screenExtended = (
      window.screen as unknown as { isExtended?: boolean }
    ).isExtended;

    if (screenExtended === true) {
      return {
        checkId: 'monitor_count',
        checkName: 'Número de Monitores',
        status: 'failed',
        details:
          'Detectamos mais de um monitor. Desconecte os monitores extras e mantenha apenas 1.',
        timestamp,
        metadata: { isExtended: true, method: 'screen.isExtended' },
      };
    }

    if (screenExtended === false) {
      return {
        checkId: 'monitor_count',
        checkName: 'Número de Monitores',
        status: 'passed',
        details: 'Apenas 1 monitor detectado.',
        timestamp,
        metadata: { isExtended: false, method: 'screen.isExtended' },
      };
    }

    const screenCount = estimateScreenCount();

    if (screenCount > MAX_MONITORS_ALLOWED) {
      return {
        checkId: 'monitor_count',
        checkName: 'Número de Monitores',
        status: 'failed',
        details:
          'Pode haver monitor extra conectado. Desconecte os extras e valide novamente.',
        timestamp,
        metadata: { screenCount, method: 'heuristic' },
      };
    }

    const windowCount = await tryWindowManagementApi();
    if (windowCount !== null) {
      if (windowCount > MAX_MONITORS_ALLOWED) {
        return {
          checkId: 'monitor_count',
          checkName: 'Número de Monitores',
          status: 'failed',
          details: `${windowCount} monitores detectados. Mantenha apenas 1 monitor para continuar.`,
          timestamp,
          metadata: { screenCount: windowCount, method: 'window-management' },
        };
      }

      return {
        checkId: 'monitor_count',
        checkName: 'Número de Monitores',
        status: 'passed',
        details: `${windowCount} monitor detectado.`,
        timestamp,
        metadata: { screenCount: windowCount, method: 'window-management' },
      };
    }

    const isDesktopSafariBrowser = isDesktopSafari();
    const warningMessage = isDesktopSafariBrowser
      ? 'No Safari não conseguimos validar monitores. Abra a prova no Google Chrome e valide novamente.'
      : 'Não foi possível validar a quantidade de monitores neste navegador. Use apenas 1 monitor e tente novamente.';

    return {
      checkId: 'monitor_count',
      checkName: 'Número de Monitores',
      status: 'warning',
      details: warningMessage,
      timestamp,
      metadata: {
        isExtended: undefined,
        method: 'unavailable',
        browser: isDesktopSafariBrowser ? 'safari-desktop' : 'unknown',
      },
    };
  } catch {
    return {
      checkId: 'monitor_count',
      checkName: 'Número de Monitores',
      status: 'warning',
      details:
        'Erro ao validar monitores. Confirme que há apenas 1 monitor e tente novamente.',
      timestamp,
    };
  }
}
